import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import moment from 'moment';
import { Observable } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AnalyticsService } from '../@core/utils';
import { UserActivityStatus } from '../enum/user-activity-status.enum';
import { UserData } from '../interface/user';
import { AuthService } from '../services/auth.service';
import { PacingPlanService } from '../services/pacing-plan.service';
import { StorageService } from '../services/storage.service';
import { ToasterService } from '../services/toaster.service';

@Injectable({
  providedIn: 'root',
})
export class AuthinterceptorService implements HttpInterceptor {
  constructor(
    private storageService: StorageService,
    private router: Router,
    private toasterService: ToasterService,
    private pacingPlanService: PacingPlanService,
    private analyticsService: AnalyticsService,
    private authService: AuthService,
    private dialog: MatDialog,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (req.reportProgress) {
      return next.handle(req);
    } else {
      const userData = this.storageService.getItem('user');
      const googleAccessToken = this.storageService.getItem('access_type');

      const bearerToken = req.url.includes('analyticsdata.googleapis.com') ? googleAccessToken : userData?.authToken;

      const cloned = this.cloneRequest(req, bearerToken);
      return !req.url.includes('api.openai.com') ||
        (!req.url.includes('analyticsdata.googleapis.com') && !userData?.authToken)
        ? this.handleRequest(next, cloned)
        : this.handleRequest(next, req);
    }
  }

  cloneRequest(req: HttpRequest<any>, authToken: string) {
    const academicYear = this.storageService.getItem('selectedSchoolCalendarByAdmin') || 0;
    let headers = req.headers;

    // Skip custom header for Xano Vault URLs (or any 3rd party URL)
    if (!req.url.includes('xano.io/vault') && !req.url.includes('ipapi.co')) {
      if (authToken) {
        headers = headers.set('Authorization', `Bearer ${authToken}`);
      }
      // Check if academicYear and academicYear.id are defined before setting the header
      // if (academicYear && academicYear.id) {
      headers = headers.set(
        'AcademicID',
        academicYear ? (academicYear.isFirst ? '0' : (academicYear.id?.toString() ?? '0')) : '0',
      );
      // }
    }

    return req.clone({ headers });
  }

  handleRequest(next: HttpHandler, value: HttpRequest<any>) {
    const unsubscribe$ = this.authService.getLogoutSignal();

    return next.handle(value).pipe(
      takeUntil(unsubscribe$),
      catchError(async (err) => {
        const error = await this.handleError(err);
        throw error;
      }),
    );
  }

  async handleError(error) {
    try {
      if (error.error.message) {
        this.analyticsService?.trackEvent(error.error.message, 'API Request Error');
        error.status !== 401 ? this.toasterService.showToast(error.error.message, 'danger') : '';

        error.status === 401 ? await this.tokenExpired() : '';
      } else {
        this.pacingPlanService.isFirstView.emit(false);
      }

      return error;
    } catch (err) {
      // console.log(err);
    }
  }

  async tokenExpired() {
    try {
      const user: UserData = this.storageService.getItem('user');
      //adding/editing existing record the record to keep track of user sessions
      const body = {
        user_id: user.user.id,
        token: user.authToken,
        expired_time: moment(),
      };
      await this.authService.addOrEditUserActivityLog(body);
      await this.updateUserStatus(user.user.id, UserActivityStatus.LOGGED_OUT);
      this.storageService.removeItem('user');
      this.dialog.closeAll();
      this.router.navigate(['/auth/login']);
    } catch (error) {
      // console.log(error)
    }
  }

  async updateUserLogOutSessionTime(user: UserData) {
    try {
      user.user['is_logged_in'] = false;

      await this.authService.updateUser(user.user.id, user.user);
      const body = {
        user_id: user.user.id,
        date: new Date().setHours(0, 0, 0, 0),
      };
      const response: any = await this.authService.getUserTimeSession(body);
      if (response && response.length > 0) {
        response[0].sessions[response[0]?.sessions?.length - 1].endTime = new Date().getTime();
        body['sessions'] = response[0].sessions;
        await this.authService.updateUserTimeSession(response[0].id, body);
      }
    } catch (error) {
      // console.log(error);
    }
  }

  public async updateUserStatus(usedId: number, status: UserActivityStatus) {
    try {
      await this.authService.updateActivityStatus({
        user_id: usedId,
        user_activity_status: status,
      });
    } catch (error) {
      console.error('Error updating user status:', error);
    }
  }
}
